import {
  pgTable,
  text,
  timestamp,
  uuid,
  integer,
  numeric,
  boolean,
  jsonb,
  index,
  check,
} from "drizzle-orm/pg-core";
import { sql } from "drizzle-orm";

// ---------------------------------------------------------------------------
// Timestamp helper
// ---------------------------------------------------------------------------

const tz = { withTimezone: true } as const;

// ---------------------------------------------------------------------------
// Service Delivery Engine — Field Service Tables
// ---------------------------------------------------------------------------
// Inspections (checklists), customer properties, property assets, service
// history, and booking media (before/after photos). These tables support
// field service niches: plumbing, HVAC, cleaning, renovation, landscaping.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Inspection Templates — configurable checklists per service type
// ---------------------------------------------------------------------------

export const INSPECTION_PHASES = [
  "pre_work",
  "post_work",
  "quality_assurance",
] as const;
export type InspectionPhase = (typeof INSPECTION_PHASES)[number];

export const inspectionTemplates = pgTable(
  "inspection_templates",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    name: text("name").notNull(),
    description: text("description"),
    serviceId: uuid("service_id"), // nullable = applies to all services
    phase: text("phase").notNull(), // InspectionPhase
    isRequired: boolean("is_required").notNull().default(false),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_insp_tpl_tenant").on(table.tenantId),
    index("idx_insp_tpl_service").on(table.tenantId, table.serviceId),
  ],
);

// ---------------------------------------------------------------------------
// Inspection Template Items — individual checklist items
// ---------------------------------------------------------------------------

export const INSPECTION_ITEM_TYPES = [
  "pass_fail",
  "numeric",
  "text",
  "photo_required",
  "select",
] as const;
export type InspectionItemType = (typeof INSPECTION_ITEM_TYPES)[number];

export const inspectionTemplateItems = pgTable(
  "inspection_template_items",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    templateId: uuid("template_id")
      .notNull()
      .references(() => inspectionTemplates.id, { onDelete: "cascade" }),
    label: text("label").notNull(),
    itemType: text("item_type").notNull(), // InspectionItemType
    options: jsonb("options").$type<string[]>(), // for select type
    isRequired: boolean("is_required").notNull().default(true),
    sortOrder: integer("sort_order").notNull().default(0),
    groupName: text("group_name"), // visual grouping: "Electrical", "Mechanical"
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_insp_item_template").on(table.tenantId, table.templateId),
  ],
);

// ---------------------------------------------------------------------------
// Booking Inspections — completed checklist instances per booking
// ---------------------------------------------------------------------------

export const INSPECTION_STATUSES = [
  "in_progress",
  "completed",
  "failed",
] as const;
export type InspectionStatus = (typeof INSPECTION_STATUSES)[number];

export const INSPECTION_RESULTS = [
  "pass",
  "fail",
  "conditional",
] as const;
export type InspectionResult = (typeof INSPECTION_RESULTS)[number];

export const bookingInspections = pgTable(
  "booking_inspections",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    bookingId: uuid("booking_id").notNull(),
    templateId: uuid("template_id")
      .notNull()
      .references(() => inspectionTemplates.id, { onDelete: "restrict" }),
    completedBy: text("completed_by"), // staff user ID
    completedAt: timestamp("completed_at", tz),
    status: text("status").notNull().default("in_progress"), // InspectionStatus
    overallResult: text("overall_result"), // InspectionResult (set on completion)
    notes: text("notes"),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_booking_insp_booking").on(table.tenantId, table.bookingId),
    index("idx_booking_insp_template").on(table.tenantId, table.templateId),
  ],
);

// ---------------------------------------------------------------------------
// Booking Inspection Responses — individual answers per checklist item
// ---------------------------------------------------------------------------

export const bookingInspectionResponses = pgTable(
  "booking_inspection_responses",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    inspectionId: uuid("inspection_id")
      .notNull()
      .references(() => bookingInspections.id, { onDelete: "cascade" }),
    templateItemId: uuid("template_item_id")
      .notNull()
      .references(() => inspectionTemplateItems.id, { onDelete: "restrict" }),
    value: text("value"), // "pass", "4.5", "Corrosion noted"
    mediaId: uuid("media_id"), // FK to booking_media if photo attached
    notes: text("notes"),
    flagged: boolean("flagged").notNull().default(false),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_insp_resp_inspection").on(table.tenantId, table.inspectionId),
  ],
);

// ---------------------------------------------------------------------------
// Customer Properties — customer locations for field service
// ---------------------------------------------------------------------------
// SECURITY: gateCode and lockboxCode are encrypted via KEK/DEK.
// NEVER log access code values — IDs only.
// GDPR: lat/lng are location PII — include in erasure path.

export const PROPERTY_TYPES = [
  "residential",
  "commercial",
  "industrial",
  "other",
] as const;
export type PropertyType = (typeof PROPERTY_TYPES)[number];

export const customerProperties = pgTable(
  "customer_properties",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    personId: uuid("person_id").notNull(),
    name: text("name"), // "Home", "Office", "Rental #3"
    addressLine1: text("address_line_1").notNull(),
    addressLine2: text("address_line_2"),
    city: text("city").notNull(),
    state: text("state"),
    zip: text("zip"),
    country: text("country").notNull().default("DE"),
    // GDPR PII — include in erasure path
    lat: numeric("lat", { precision: 10, scale: 7 }),
    lng: numeric("lng", { precision: 10, scale: 7 }),
    propertyType: text("property_type").notNull().default("residential"), // PropertyType
    squareFootage: integer("square_footage"),
    yearBuilt: integer("year_built"),
    accessNotes: text("access_notes"), // "Dog in backyard, ring bell twice"
    // SECURITY: encrypted via KEK/DEK — NEVER log values
    gateCodeEncrypted: text("gate_code_encrypted"),
    lockboxCodeEncrypted: text("lockbox_code_encrypted"),
    encryptionKeyId: text("encryption_key_id"), // KEK version
    parkingInstructions: text("parking_instructions"),
    isActive: boolean("is_active").notNull().default(true),
    metadata: jsonb("metadata").$type<Record<string, unknown>>(),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_cust_prop_tenant").on(table.tenantId),
    index("idx_cust_prop_person").on(table.tenantId, table.personId),
  ],
);

// ---------------------------------------------------------------------------
// Property Assets — equipment/appliances tracked at customer properties
// ---------------------------------------------------------------------------

export const ASSET_CONDITIONS = [
  "excellent",
  "good",
  "fair",
  "poor",
  "needs_replacement",
] as const;
export type AssetCondition = (typeof ASSET_CONDITIONS)[number];

export const propertyAssets = pgTable(
  "property_assets",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    propertyId: uuid("property_id")
      .notNull()
      .references(() => customerProperties.id, { onDelete: "cascade" }),
    assetType: text("asset_type").notNull(), // "hvac_unit", "water_heater", etc.
    manufacturer: text("manufacturer"),
    modelNumber: text("model_number"),
    serialNumber: text("serial_number"),
    installDate: timestamp("install_date", tz),
    warrantyExpiry: timestamp("warranty_expiry", tz),
    warrantyProvider: text("warranty_provider"),
    condition: text("condition").notNull().default("good"), // AssetCondition
    locationInProperty: text("location_in_property"), // "Attic", "Basement"
    lastServiceDate: timestamp("last_service_date", tz),
    nextServiceDue: timestamp("next_service_due", tz),
    notes: text("notes"),
    metadata: jsonb("metadata").$type<Record<string, unknown>>(),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_prop_asset_tenant").on(table.tenantId),
    index("idx_prop_asset_property").on(table.tenantId, table.propertyId),
    index("idx_prop_asset_next_service").on(
      table.tenantId,
      table.nextServiceDue,
    ),
  ],
);

// ---------------------------------------------------------------------------
// Asset Service History — APPEND-ONLY service log per asset
// ---------------------------------------------------------------------------
// IMMUTABILITY: Append-only at service layer. No UPDATE, no DELETE.
// Each entry is a permanent record of work performed on an asset.

export const SERVICE_TYPES = [
  "maintenance",
  "repair",
  "install",
  "inspection",
  "replacement",
] as const;
export type AssetServiceType = (typeof SERVICE_TYPES)[number];

export interface PartsUsed {
  name: string;
  quantity: number;
  costCents: number;
}

export const assetServiceHistory = pgTable(
  "asset_service_history",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    assetId: uuid("asset_id")
      .notNull()
      .references(() => propertyAssets.id, { onDelete: "cascade" }),
    bookingId: uuid("booking_id"), // FK to service_bookings (nullable for manual entries)
    serviceDate: timestamp("service_date", tz).notNull(),
    serviceType: text("service_type").notNull(), // AssetServiceType
    description: text("description"),
    technicianId: text("technician_id"), // staff user ID
    partsUsed: jsonb("parts_used").$type<PartsUsed[]>(),
    costCents: integer("cost_cents"),
    nextRecommendedService: text("next_recommended_service"),
    nextServiceDue: timestamp("next_service_due", tz),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    // No updatedAt — append-only
  },
  (table) => [
    index("idx_asset_svc_history_asset").on(table.tenantId, table.assetId),
    index("idx_asset_svc_history_booking").on(table.tenantId, table.bookingId),
  ],
);

// ---------------------------------------------------------------------------
// Booking Media — before/during/after photos and documents
// ---------------------------------------------------------------------------
// GPS coordinates (lat/lng) from EXIF prove location for field service.
// GDPR: lat/lng are PII — include in erasure path.

export const MEDIA_PHASES = [
  "before",
  "during",
  "after",
  "nameplate",
  "signature",
] as const;
export type MediaPhase = (typeof MEDIA_PHASES)[number];

export const MEDIA_TYPES = [
  "photo",
  "video",
  "document",
] as const;
export type MediaType = (typeof MEDIA_TYPES)[number];

export const bookingMedia = pgTable(
  "booking_media",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    bookingId: uuid("booking_id"), // FK to service_bookings
    jobId: uuid("job_id"), // FK to jobs
    phase: text("phase").notNull(), // MediaPhase
    mediaType: text("media_type").notNull(), // MediaType
    r2Key: text("r2_key").notNull(),
    thumbnailR2Key: text("thumbnail_r2_key"),
    caption: text("caption"),
    capturedAt: timestamp("captured_at", tz).notNull(),
    capturedBy: text("captured_by"), // staff user ID
    // GDPR PII — GPS from EXIF proves location. Include in erasure path.
    lat: numeric("lat", { precision: 10, scale: 7 }),
    lng: numeric("lng", { precision: 10, scale: 7 }),
    isCustomerVisible: boolean("is_customer_visible").notNull().default(false),
    sortOrder: integer("sort_order").notNull().default(0),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_booking_media_booking").on(table.tenantId, table.bookingId),
    index("idx_booking_media_job").on(table.tenantId, table.jobId),
    check(
      "chk_booking_media_ref",
      sql`${table.bookingId} IS NOT NULL OR ${table.jobId} IS NOT NULL`,
    ),
  ],
);
